import { useCallback, useEffect, useRef, useState } from 'react';
import { useVoiceDemo } from './useExclusiveAudio';

// Browser voice chat with Tessa — the same AI receptionist as the phone demo line,
// relayed through tessa-web on our server (Gemini Live). Mic audio goes up as
// 16 kHz PCM16; Tessa's voice comes back as 24 kHz PCM16.
const BASE = 'https://audit.ubotika.site/voice-web';
const WS_BASE = 'wss://audit.ubotika.site/voice-web/ws';

// Runs in the audio thread: forwards mic samples, downsampled to 16 kHz PCM16,
// in 100 ms chunks (the audio thread ticks ~375x/s; the server caps frames/sec).
const WORKLET = `
class MicDown extends AudioWorkletProcessor {
  constructor() { super(); this.ratio = sampleRate / 16000; this.acc = []; this.pos = 0; this.pending = []; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) this.acc.push(ch[i]);
    const out = [];
    while (this.pos + this.ratio <= this.acc.length) {
      const start = Math.floor(this.pos), end = Math.floor(this.pos + this.ratio);
      let sum = 0; for (let j = start; j < end; j++) sum += this.acc[j];
      out.push(sum / Math.max(1, end - start));
      this.pos += this.ratio;
    }
    const used = Math.floor(this.pos);
    this.acc = this.acc.slice(used); this.pos -= used;
    for (let i = 0; i < out.length; i++) this.pending.push(out[i]);
    if (this.pending.length >= 1600) {
      const pcm = new Int16Array(this.pending.length);
      for (let i = 0; i < this.pending.length; i++) { const s = Math.max(-1, Math.min(1, this.pending[i])); pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff; }
      this.pending = [];
      this.port.postMessage(pcm.buffer, [pcm.buffer]);
    }
    return true;
  }
}
registerProcessor('mic-down', MicDown);
`;

// Proof-of-work: find a nonce so sha256(salt:nonce) starts with `bits` zero bits.
// Costs a real visitor about a second; makes scripted abuse expensive.
async function solve(salt, bits) {
  const enc = new TextEncoder();
  for (let nonce = 0; nonce < 50000000; nonce++) {
    const d = new Uint8Array(await crypto.subtle.digest('SHA-256', enc.encode(`${salt}:${nonce}`)));
    let zeros = 0;
    for (const byte of d) {
      if (byte === 0) { zeros += 8; continue; }
      zeros += Math.clz32(byte) - 24;
      break;
    }
    if (zeros >= bits) return nonce;
  }
  throw new Error('pow');
}

const MESSAGES = {
  busy: "Tessa can't take a web chat right now. Call the number below to talk to her.",
  mic: 'Microphone access is needed to talk to Tessa. Allow it in your browser, or call the number below.',
  unsupported: "This browser can't run the voice chat. Call the number below instead.",
  generic: "Couldn't connect. Call the number below instead.",
};

export function useTessaWebCall({ onStart } = {}) {
  const voice = useVoiceDemo();
  const [status, setStatus] = useState('idle'); // idle | connecting | live | error
  const [error, setError] = useState('');
  const refs = useRef({});

  const cleanup = useCallback(() => {
    const r = refs.current;
    try { if (r.ws && r.ws.readyState === 1) r.ws.send(JSON.stringify({ type: 'hangup' })); } catch (e) { /* closing anyway */ }
    try { if (r.ws) r.ws.close(); } catch (e) { /* closing anyway */ }
    (r.sources || []).forEach((s) => { try { s.stop(); } catch (e) { /* already stopped */ } });
    try { if (r.stream) r.stream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    try { if (r.ctx) r.ctx.close(); } catch (e) { /* ignore */ }
    refs.current = {};
  }, []);

  const end = useCallback(() => {
    cleanup();
    voice.close();
    setStatus('idle');
  }, [cleanup, voice]);

  // Another audio source (the ElevenLabs widget, a video with sound) took over: hang up.
  useEffect(() => {
    if ((status === 'live' || status === 'connecting') && !voice.open) {
      cleanup();
      setStatus('idle');
    }
  }, [voice.open, status, cleanup]);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    if (status === 'connecting' || status === 'live') return;
    setError('');
    if (!window.AudioWorkletNode || !navigator.mediaDevices?.getUserMedia || !window.crypto?.subtle) {
      setError(MESSAGES.unsupported);
      setStatus('error');
      return;
    }
    setStatus('connecting');
    if (!voice.open) voice.toggle();
    if (onStart) onStart();
    const r = {};
    refs.current = r;
    const fail = (code) => {
      if (refs.current !== r) return;
      cleanup();
      voice.close();
      setError(MESSAGES[code] || MESSAGES.generic);
      setStatus('error');
    };
    try {
      r.ctx = new (window.AudioContext || window.webkitAudioContext)();
      await r.ctx.resume();
      try {
        r.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch (e) {
        fail('mic');
        return;
      }
      const res = await fetch(`${BASE}/challenge`, { cache: 'no-store' });
      if (!res.ok) { fail(res.status === 429 ? 'busy' : 'generic'); return; }
      const challenge = await res.json();
      const nonce = await solve(challenge.salt, challenge.bits);
      if (refs.current !== r) return; // hung up while solving

      const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
      await r.ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);

      const qs = new URLSearchParams({
        salt: challenge.salt,
        exp: String(challenge.exp),
        sig: challenge.sig,
        nonce: String(nonce),
        page: window.location.pathname,
      });
      const ws = new WebSocket(`${WS_BASE}?${qs}`);
      ws.binaryType = 'arraybuffer';
      r.ws = ws;
      r.sources = [];
      r.playAt = 0;

      const mic = r.ctx.createMediaStreamSource(r.stream);
      const node = new AudioWorkletNode(r.ctx, 'mic-down');
      node.port.onmessage = (ev) => { if (ws.readyState === 1 && r.live) ws.send(ev.data); };
      mic.connect(node);
      // Keep the worklet running without playing the mic back to the speakers.
      const silent = r.ctx.createGain();
      silent.gain.value = 0;
      node.connect(silent).connect(r.ctx.destination);

      let opened = false;
      ws.onopen = () => { opened = true; };
      ws.onmessage = (ev) => {
        if (typeof ev.data === 'string') {
          let msg;
          try { msg = JSON.parse(ev.data); } catch (e) { return; }
          if (msg.type === 'ready') { r.live = true; setStatus('live'); }
          if (msg.type === 'interrupted') {
            r.sources.forEach((s) => { try { s.stop(); } catch (e) { /* done */ } });
            r.sources = [];
            r.playAt = 0;
          }
          return;
        }
        const pcm = new Int16Array(ev.data);
        if (!pcm.length) return;
        const buf = r.ctx.createBuffer(1, pcm.length, 24000);
        const ch = buf.getChannelData(0);
        for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 0x8000;
        const src = r.ctx.createBufferSource();
        src.buffer = buf;
        src.connect(r.ctx.destination);
        const at = Math.max(r.ctx.currentTime + 0.03, r.playAt);
        src.start(at);
        r.playAt = at + buf.duration;
        r.sources.push(src);
        src.onended = () => { r.sources = r.sources.filter((s) => s !== src); };
      };
      ws.onclose = () => {
        if (refs.current !== r) return;
        // A rejected connection never opens — almost always a limit.
        if (!opened) { fail('busy'); return; }
        // Let Tessa finish her last sentence before tearing down.
        const wait = Math.max(0, (r.playAt - r.ctx.currentTime) * 1000);
        setTimeout(() => {
          if (refs.current !== r) return;
          cleanup();
          voice.close();
          setStatus('idle');
        }, wait);
      };
    } catch (e) {
      fail('generic');
    }
  }, [status, voice, onStart, cleanup]);

  return { status, error, start, end };
}
